import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../shared/services/api.service';
import { CustomerInterface } from '../models/customer.interface';
import { RibInterface } from '../models/rib.interface';
import { UpdatePasswordInterface } from '../models/update-password.interface';
import { ExtranetService } from '../../shared/services/extranet.service';
import { MfaInterface } from '../../shared/models/mfa.interface';
import { ChangeOfferDateInterface } from '../models/change-offer-date.interface';
import { ConstsHelper } from '../../../helpers/consts.helper';
import { KycReportInterface } from '../models/kyc-report.interface';
import { ContractSignatoryInterface } from '../models/contract-signatory.interface';
import { VatNumberStatusInterface } from '../../shared/models/vat-number-status.interface';
import { CustomerVatInterface } from '../models/customer-vat.interface';

@Injectable()
export class CustomerService {

  constructor(
      private apiService: ApiService,
      private extranetService: ExtranetService
  ) {}

  /**
   * Get customer data
   */
  get(): Observable<CustomerInterface> {
    return this.apiService.get<CustomerInterface>('customer');
  }

  /**
   * Get customer session
   */
  getSession(): Observable<{session: string}> {
    return this.apiService.get<{session: string}>('session/customer');
  }

  /**
   * Update customer address data
   * @param customer
   * @param mfa
   */
  updateAddress(customer: CustomerInterface, mfa: MfaInterface|null = null): Observable<any> {
    let data = {
      street: customer.street,
      zipCode: customer.zipCode,
      city: customer.city,
      addressComplement: customer.addressComplement,
      placeName: customer.placeName,
      byPassGoogleCheck: true,
      password: customer.password,
    };
    if (mfa) {
      data = Object.assign({ action: mfa.action, code: mfa.code, destination: mfa.destination }, data);
    }
    let url = 'ec/customer/address';
    if (this.extranetService.getSupportUser()) {
      url = 'extranet/customer/address';
    }
    return this.apiService.put(url, data);
  }

  /**
   * Update personnel information customer data
   * @param email
   * @param phoneNumber
   * @param routingAddress
   * @param codeService
   * @param numeroCommande
   * @param buyerReference
   * @param password
   * @param mfa
   */
  updatePersonnelInformation(
      email: string,
      phoneNumber: string,
      routingAddress?: string,
      codeService?: string,
      numeroCommande?: string,
      buyerReference?: string,
      password?: string,
      mfa: MfaInterface|null = null
  ): Observable<any> {
    let url = 'ec/customer/phone-number/email';
    if (this.extranetService.getSupportUser()) {
      url = 'extranet/customer/phone-number/email';
    }
    let body = {password, email, phoneNumber, routingAddress, codeService, numeroCommande, buyerReference};
    if (mfa) {
      body = Object.assign({ action: mfa.action, code: mfa.code, destination: mfa.destination }, body);
    }
    return this.apiService.put(url, body);
  }

  /**
   * Update rib data
   */
  updateRib(data: RibInterface): Observable<any> {
    let url = 'ec/customer/iban-bic';
    if (this.extranetService.getSupportUser()) {
      url = 'extranet/customer/iban-bic';
    } else if (localStorage.getItem(ConstsHelper.LOCAL_STORAGE_ADMIN_PRO_FEET_TOKEN)) {
      url = 'ec/pro/fleet/customer/iban-bic';
    }
    return this.apiService.put(url, data);
  }

  /**
   * Update rib data
   * @param data
   * @param mfa
   */
  updatePassword(data: UpdatePasswordInterface, mfa: MfaInterface|null = null): Observable<any> {
    let body = data;
    if (mfa) {
      body = Object.assign({ action: mfa.action, code: mfa.code, destination: mfa.destination }, data);
    }
    return this.apiService.put('customer/password', body);
  }

  /**
   * Check account consolidation
   */
  checkAccountConsolidation(customerId: string, lastName: string): Observable<any> {
    return this.apiService.get('customer/check/account/consolidation/' + customerId + '/' + lastName);
  }

  /**
   * Check password
   */
  checkPassword(password: string): Observable<any> {
    return this.apiService.post('customer/check/password', {password});
  }

  /**
   * Delete iban
   */
  deleteIban(): Observable<any> {
    return this.apiService.post('extranet/delete/iban-bic', {});
  }

  /**
   * Used in subscription standalone page
   */
  setIbanBic(data: RibInterface): Observable<any> {
    return this.apiService.put('customer/iban-bic', data);
  }

  /**
   * Get change offer date
   */
  getChangeOfferDate(): Observable<ChangeOfferDateInterface> {
    return this.apiService.get<ChangeOfferDateInterface>('change/offer/date');
  }

  /**
   * Check email validity
   */
  checkEmailValidity(email: string): Observable<any> {
    return this.apiService.get<any>('vitrine/customer/check-email/' + email);
  }

  /**
   * Check iban
   */
  checkIban(iban: string): Observable<any> {
    return this.apiService.post<any>('extranet/check/iban', { iban });
  }

  /**
   * Update full information
   */
  updateFullInformation(data: any): Observable<any> {
    return this.apiService.put<any>('customer/full/information', data);
  }

  /**
   * Get customer rgpd
   */
  getCustomerRgpd(): Observable<any> {
    return this.apiService.get<any>('customer/rgpd');
  }

  /**
   * @param msisdn
   */
  getReport(msisdn: string): Observable<Array<KycReportInterface>> {
    return this.apiService.get<Array<KycReportInterface>>('kyc/reports/'+msisdn);
  }

  /**
   * Signatories list
   */
  getSignatories(): Observable<Array<ContractSignatoryInterface>> {
    return this.apiService.get<Array<ContractSignatoryInterface>>('customer/contract/signatories');
  }

  /**
   * Create Signatory
   */
  createSignatory(data: ContractSignatoryInterface): Observable<any> {
    return this.apiService.post<any>('customer/contract/signatory', data);
  }

  /**
   * Update Signatory
   */
  updateSignatory(data: ContractSignatoryInterface): Observable<any> {
    return this.apiService.put<any>('customer/contract/signatory', data);
  }

  /**
   * Delete Signatory
   */
  deleteSignatory(id: number): Observable<any> {
    return this.apiService.delete<any>('customer/contract/signatory/' + id);
  }

  /**
   * Get vat number status
   */
  getVatNumberStatus(): Observable<VatNumberStatusInterface> {
    return this.apiService.get<VatNumberStatusInterface>('check/vat/number/status');
  }

  /**
   * Get routing address list
   *
   * @param siret
   */
  getRoutingAddressList(siret: string): Observable<Array<{ value: string; label:string; }>> {
    return this.apiService.get<Array<{ value: string; label:string; }>>('routing/address/list/' + siret);
  }

  /**
   * Get customer VAT
   */
  getCustomerVat(): Observable<CustomerVatInterface> {
    return this.apiService.get<CustomerVatInterface>('customer/vat');
  }
}
